/**
 * Choropleth: infer which ECharts geo registration to load and map dimension values
 * to feature names inside that GeoJSON.
 */

/** ECharts registration names passed to series.map — must match registerMap(...) */
export const CHOROPLETH_REGISTERED_MAP_IDS = /** @type {const} */ (['world', 'USA']);

/**
 * USPS / territory codes → `name` property in Apache USA.json (states + DC + PR).
 * @see https://echarts.apache.org/examples/data/asset/geo/USA.json
 */
const USPS_TO_USA_FEATURE_NAME = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
    PR: 'Puerto Rico',
};

const USPS_SET = new Set(Object.keys(USPS_TO_USA_FEATURE_NAME));

/**
 * @param {string | undefined | null} raw
 * @returns {'world' | 'USA'}
 */
export function normalizeExplicitGeoMap(raw) {
    if (raw == null || raw === '') return null;
    const s = String(raw).trim().toLowerCase();
    if (s === 'usa' || s === 'us' || s === 'united states') return 'USA';
    if (s === 'world' || s === 'global') return 'world';
    const up = String(raw).trim().toUpperCase();
    if (up === 'USA') return 'USA';
    return null;
}

/**
 * @param {unknown} dimField
 * @returns {string}
 */
export function choroplethDimensionLabel(dimField) {
    if (dimField == null) return '';
    if (typeof dimField === 'string') return dimField.trim();
    if (typeof dimField === 'object' && dimField !== null) {
        const o = dimField;
        return String(o.field || o.name || o.column || o.id || '').trim();
    }
    return '';
}

/**
 * Infer map preset when rows are empty (configure screen) from dimension naming.
 * @param {unknown} dimField widget config.xAxis[0]
 * @returns {'world' | 'USA' | null}
 */
export function geoMapHintFromDimensionField(dimField) {
    const raw = choroplethDimensionLabel(dimField);
    const lc = raw.toLowerCase();
    if (!lc) return null;
    if (/\b(country|countries|continent|continentcode|nationality|iso\s*-?\s*2|iso\s*-?\s*3|alpha\s*-?\s*2)\b/.test(lc)) {
        return 'world';
    }
    const usaLikely =
        /\bpatient\s*state\b|\bpatientstate\b|\bpatient_state\b|\bprovincestate\b|\bstateprovince\b|\bmailingstate\b|\bship(?:ping)?state\b|\bstate_?(code|abbr|abbrev)\b|\bus_?postal\b/.test(lc) ||
        /(^|[._])stateshort(\b|$)/i.test(lc) ||
        /(^|[._])[a-z0-9]+state\b/i.test(lc);
    return usaLikely ? 'USA' : null;
}

/**
 * @param {Array<{ x_val?: unknown }>} rows Builder/chart rows (after x_val mapping).
 * @param {string | undefined} explicit geoMap from widget config.data.geoMap
 * @param {unknown} [xAxisDimensionField] First dimension field (hints map before rows load).
 */
export function inferRegisteredMapId(rows = [], explicit, xAxisDimensionField = null) {
    const fromConfig = normalizeExplicitGeoMap(explicit);
    if (fromConfig) return fromConfig;

    const values = rows
        .map((r) => r?.x_val)
        .filter((v) => v != null && String(v).trim() !== '')
        .map((v) => String(v).trim());

    const dimHint = geoMapHintFromDimensionField(xAxisDimensionField);

    if (values.length === 0) {
        return dimHint || 'world';
    }

    const upper2 = values.filter((v) => /^[A-Za-z]{2}$/.test(v)).map((v) => v.toUpperCase());

    const twoFrac = upper2.length / values.length;

    let usHits = 0;
    upper2.forEach((code) => {
        if (USPS_SET.has(code)) usHits += 1;
    });
    const usFracIn2 = upper2.length ? usHits / upper2.length : 0;

    // Strong signal: mostly 2-letter codes that match US subdivisions → USA map.
    if (upper2.length >= 3 && twoFrac >= 0.65 && usFracIn2 >= 0.72) return 'USA';

    // Small samples + State-style dimension: still prefer USA map.
    if (twoFrac >= 0.85 && upper2.length >= 1 && dimHint === 'USA' && usFracIn2 >= 0.45) return 'USA';

    return dimHint || 'world';
}

/**
 * Map a dimension value to the GeoJSON feature `name` for the chosen registration.
 * @param {unknown} raw
 * @param {'world' | 'USA'} registeredMapId
 */
export function choroplethRegionFeatureName(raw, registeredMapId) {
    if (raw == null) return '';
    const s = String(raw).trim();
    if (!s) return '';

    if (registeredMapId === 'USA') {
        if (/^[A-Za-z]{2}$/.test(s)) {
            const nm = USPS_TO_USA_FEATURE_NAME[s.toUpperCase()];
            return nm || s;
        }
        return s;
    }

    return s;
}
